import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import './ToolPage.css'
import './FileShareCreate.css'
import { uploadFileShare, deleteFileShare } from './utils/fileShareApiClient'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const TTL_OPTIONS = [
  { hours: 1, label: '1 hora' },
  { hours: 6, label: '6 horas' },
  { hours: 24, label: '24 horas (1 dia)' },
  { hours: 72, label: '72 horas (3 dias)' },
  { hours: 168, label: '168 horas (7 dias)' },
]

const MAX_SIZE_MB = 200

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

const FileShareCreate = () => {
  useDocumentMeta({
    title: 'Compartilhamento de Arquivo Temporário | jarder.dev',
    description:
      'Envie um arquivo grande e receba um link que expira sozinho depois de ' +
      'N downloads ou X horas, o que vier primeiro. Grátis.',
  })

  const [file, setFile] = useState(null)
  const [maxDownloads, setMaxDownloads] = useState(5)
  const [ttlHours, setTtlHours] = useState(24)
  const [progress, setProgress] = useState(0)
  const [status, setStatus] = useState('idle')
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)
  const [copied, setCopied] = useState(false)
  const [deleted, setDeleted] = useState(false)

  const handleFile = (event) => {
    const selected = event.target.files[0]
    if (!selected) return
    if (selected.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`Esse arquivo passa do limite de ${MAX_SIZE_MB}MB.`)
      setFile(null)
      return
    }
    setError('')
    setFile(selected)
    setResult(null)
    setDeleted(false)
  }

  const upload = async () => {
    if (!file) return
    setStatus('loading')
    setProgress(0)
    setError('')
    try {
      const { id, ownerToken } = await uploadFileShare(file, maxDownloads, ttlHours, setProgress)
      setResult({ id, ownerToken, name: file.name, size: file.size })
      trackEvent('tool_generate', { tool: 'file_share', maxDownloads, ttlHours })
    } catch (err) {
      setError(err.message || 'Não foi possível enviar o arquivo agora.')
    } finally {
      setStatus('idle')
    }
  }

  const shareUrl = result ? `${window.location.origin}/ferramentas/compartilhar-arquivo/${result.id}` : ''

  const copy = () => {
    navigator.clipboard.writeText(shareUrl)
    setCopied(true)
    trackEvent('tool_copy', { tool: 'file_share' })
  }

  const removeNow = async () => {
    if (!result) return
    try {
      await deleteFileShare(result.id, result.ownerToken)
      setDeleted(true)
    } catch (err) {
      setError(err.message || 'Não foi possível excluir agora.')
    }
  }

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Compartilhamento de Arquivo Temporário</h1>
      <p className="tool-disclaimer">
        Envie um arquivo de até {MAX_SIZE_MB}MB e receba um link. O link
        expira sozinho depois do número de downloads escolhido ou do tempo
        escolhido, o que vier primeiro.
      </p>

      <div className="tool-field">
        <label htmlFor="share-file-input">Arquivo</label>
        <input id="share-file-input" type="file" onChange={handleFile} />
        {file && <span className="tool-import-hint">{file.name} — {formatSize(file.size)}</span>}
      </div>

      <div className="tool-row">
        <div className="tool-field">
          <label htmlFor="max-downloads">Expira depois de quantos downloads</label>
          <input
            id="max-downloads"
            type="number"
            min="1"
            max="50"
            value={maxDownloads}
            onChange={(e) => setMaxDownloads(Number(e.target.value))}
          />
        </div>
        <div className="tool-field">
          <label htmlFor="ttl-hours">Ou expira depois de quanto tempo</label>
          <select id="ttl-hours" value={ttlHours} onChange={(e) => setTtlHours(Number(e.target.value))}>
            {TTL_OPTIONS.map((option) => (
              <option key={option.hours} value={option.hours}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <button type="button" className="tool-button" onClick={upload} disabled={!file || status === 'loading'}>
        {status === 'loading' ? 'Enviando...' : 'Enviar e gerar link'}
      </button>

      {status === 'loading' && (
        <div className="file-share-progress">
          <div className="file-share-progress-bar" style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
      )}

      {error && <p className="tool-error">{error}</p>}

      {result && !deleted && (
        <div className="tool-result-card" style={{ marginTop: 20 }}>
          <span className="tool-result-label">Link de compartilhamento</span>
          <span className="tool-result-value" style={{ wordBreak: 'break-all' }}>
            {shareUrl}
          </span>
          <p className="tool-import-hint">
            Expira depois de {maxDownloads} download(s) ou {TTL_OPTIONS.find((o) => o.hours === ttlHours)?.label.toLowerCase()}, o que vier primeiro.
          </p>
          <div className="tool-result-actions">
            <button type="button" className="tool-button tool-button-secondary" onClick={copy}>
              {copied ? 'Copiado!' : 'Copiar link'}
            </button>
            <button type="button" className="tool-button tool-button-secondary" onClick={removeNow}>
              Excluir agora
            </button>
          </div>
        </div>
      )}

      {deleted && <p className="tool-import-hint">Arquivo excluído. O link não funciona mais.</p>}

      <div className="tool-info-section">
        <h2>Como funciona</h2>
        <p>
          O arquivo fica guardado só até o limite escolhido ser atingido —
          depois disso, é apagado de vez e o link para de funcionar. Só
          quem tiver o link consegue baixar, e cada download é contado
          direto no servidor.
        </p>
      </div>
    </div>
  )
}

export default FileShareCreate
